import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PollingIntervalControl, {
  POLLING_INTERVAL_DEFAULT_MS,
  formatPollingInterval,
} from 'features/integracoes/components/PollingIntervalControl';
import useComercialRanking, { chaveDoRecorte } from 'features/comercial/hooks/useComercialRanking';
import CelebracaoDeMeta from 'features/comercial/components/CelebracaoDeMeta';
import CenasDoRanking from 'features/comercial/components/CenasDoRanking';
import CorridaDeHoje from 'features/comercial/components/CorridaDeHoje';
import EditorDeFotoDoVendedor from 'features/comercial/components/EditorDeFotoDoVendedor';
import { preparar as prepararCenas } from 'features/comercial/cenas/cenasDoRanking';
import {
  CHAMADA_DE_VENDA,
  CHAMADA_DA_META_DO_DIA,
  CHAMADA_DA_SUPERMETA_DO_DIA,
  chamadaDaCena,
  corDoVendedor,
  corredoresDeHoje,
  detectarMarcos,
  detectarUltrapassagens,
  detectarVendasNovas,
  detectarMetasDoDia,
  MAXIMO_DE_RAIAS,
  enfileirarCenas,
  montarFilaDeCenas,
  nomeParaCena,
  retratoDaCorrida,
  retratoDasMetas,
} from 'features/comercial/cenas/eventosDoRanking';
import { usePermissions } from 'hooks/usePermissions';
import {
  AlertTriangle,
  Award,
  Camera,
  Check,
  Clapperboard,
  Crown,
  Maximize2,
  Medal,
  Minimize2,
  Play,
  Sparkles,
  Target,
  Trophy,
  Volume2,
  VolumeX,
} from 'lucide-react';

const numero = (valor) => Number(valor || 0).toLocaleString('pt-BR');
const vendasPorExtenso = (valor) => `${numero(valor)} ${Number(valor) === 1 ? 'venda' : 'vendas'}`;
const MIN_REFRESH_FEEDBACK_MS = 3000;

/**
 * Teto do backoff. Trinta minutos é longo o bastante para não martelar uma origem fora do ar e
 * curto o bastante para a tela se recuperar sozinha dentro de um turno de trabalho.
 */
const BACKOFF_TETO_MS = 30 * 60 * 1000;

/**
 * Piso do seletor de intervalo desta tela, alinhado ao TTL do cache da leitura direta
 * (`ComercialDirectRead::CACHE_TTL_SECONDS`, 300 s). Abaixo disso o seletor prometeria uma
 * frequência que a arquitetura não entrega — sem `force`, quatro de cada cinco ciclos devolveriam
 * a mesma entrada de cache.
 */
const POLLING_MINIMO_RANKING_MS = 5 * 60 * 1000;

/**
 * O período "Até agora (mês)" como ele é HOJE, e não como era quando a página foi aberta.
 *
 * A página do Comercial calcula início e fim do período uma vez, ao abrir. Numa TV que passa a
 * noite ligada, o fim ficava em ontem: a corrida de hoje seguia andando (ela lê o relógio a cada
 * ciclo), mas os cards do período, as barras de meta e toda cena que depende deles — faixa, meta,
 * supermeta — paravam de receber venda nova, sem erro nenhum. Só o período relativo é ajustado;
 * datas escolhidas à mão ficam como estão.
 */
export const periodoQueAcompanhaODia = (filtro, hoje) => {
  if (filtro?.presetId !== 'mtd' || !hoje || filtro.end === hoje) return filtro;

  return { ...filtro, start: `${hoje.slice(0, 8)}01`, end: hoje };
};

/**
 * Dia de hoje no fuso LOCAL, no formato que a API recebe.
 *
 * `toISOString()` é UTC: depois das 21h em Santarém já é o dia seguinte em UTC, e o ranking de hoje
 * passaria as três últimas horas do expediente mostrando um dia que ainda não começou.
 */
export const diaDeHoje = (agora = new Date()) =>
  `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;

/**
 * Cenas e som são escolha de quem cuida da TV, e ficam guardadas nela: reiniciar o painel não pode
 * religar o som de uma sala que pediu silêncio. Ligados por padrão.
 */
const CHAVE_DAS_CENAS = 'ranking-comercial:cenas';
const CHAVE_DO_SOM = 'ranking-comercial:som';

const lerPreferencia = (chave) => {
  try {
    return window.localStorage.getItem(chave) !== 'nao';
  } catch (erro) {
    return true;
  }
};

const gravarPreferencia = (chave, ligado) => {
  try {
    window.localStorage.setItem(chave, ligado ? 'sim' : 'nao');
  } catch (erro) {
    // Sem armazenamento a escolha vale só até a recarga.
  }
};

const prefereMenosMovimento = () => {
  try {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  } catch (erro) {
    return false;
  }
};

/**
 * A tela de apresentação com a corrida de hoje foi desenhada para 1080 px de altura, e é escalada
 * para a altura que houver.
 *
 * Pódio, lista e seis raias só cabem juntos porque cada um tem a sua medida; numa TV de 768 px o
 * pódio era esmagado até sumir, e numa 4K sobrava metade da tela. Escalar o conjunto mantém a
 * proporção em qualquer TV 16:9 — e em janela que não está em tela cheia, que é como o painel
 * costuma ser aberto antes de alguém apertar F11.
 */
const ALTURA_DE_REFERENCIA_DA_TV = 1080;

export const escalaDaTv = (alturaDaJanela) => {
  const escala = Number(alturaDaJanela) / ALTURA_DE_REFERENCIA_DA_TV;

  return Number.isFinite(escala) && escala > 0 ? Math.max(0.5, Math.min(3, escala)) : 1;
};

/** Onde ficam fontes, logo e sons das cenas. */
const RECURSOS_DAS_CENAS = `${process.env.PUBLIC_URL || ''}/ranking-cenas/`;

/** Toca um dos sons de `public/ranking-cenas/chamadas/`. Sem áudio, a tela segue igual. */
const tocarSomDoRanking = (nome) => {
  try {
    const audio = new Audio(`${RECURSOS_DAS_CENAS}chamadas/${nome}.mp3`);
    audio.volume = 0.85;
    const tocando = audio.play();
    if (tocando && tocando.catch) tocando.catch(() => {});
  } catch (erro) {
    // Navegador sem áudio, ou som ainda bloqueado por falta de um clique na página.
  }
};

/** Acima disto a fila usa a versão curta de cada cena: três de 8 s seguidas prendem a TV demais. */
const CENAS_LONGAS_POR_CICLO = 2;

// Reexportado: os testes do painel conferem o nome pelo mesmo caminho que a tela usa.
export { nomeParaCena };

/**
 * A altura da janela em modo apresentação.
 *
 * `dvh` desconta as barras do navegador no celular, mas só existe do Chromium 108 em diante. Onde
 * não há, a declaração inteira é descartada e o painel ficava sem altura — vale o `vh` de sempre.
 */
const ALTURA_DA_TELA = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height', '100dvh')
  ? '100dvh'
  : '100vh';

/**
 * O fundo do painel: um degradê escuro tingido com a cor da empresa (`--accent`).
 *
 * O tingimento usa `color-mix`, que só existe do Chromium 111 em diante — e a declaração tem um
 * `var()` dentro. Isso muda a forma de falhar: valor com `var()` o navegador NÃO consegue recusar
 * ao ler; ele aceita, e só descobre que é inválido na hora de calcular. Aí o fundo não volta para
 * a cor da classe (`bg-slate-950`): vira o valor inicial, TRANSPARENTE. Na TV da operação
 * (Chromium 79) o painel em tela cheia ficava sem fundo, e o cabeçalho e o filtro da página
 * apareciam através dele.
 *
 * Por isso a escolha é feita aqui, perguntando ao navegador, e não deixada para o CSS: onde não há
 * `color-mix`, o fundo é um degradê escuro sem a cor da empresa.
 */
const FUNDO_DO_PAINEL = typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
  && CSS.supports('color', 'color-mix(in srgb, red 50%, blue)')
  ? 'radial-gradient(circle at 16% 8%, color-mix(in srgb, var(--accent) 34%, transparent), transparent 34%), radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--accent) 24%, transparent), transparent 30%), linear-gradient(135deg, color-mix(in srgb, var(--accent) 20%, #020617), #050816 45%, #09060f)'
  : 'linear-gradient(135deg, #0b1230, #050816 45%, #09060f)';

/** Sem movimento do mouse nem tecla por este tempo, a TV esconde os controles e o cursor. */
const OCIOSO_APOS_MS = 4000;

const horaCurta = (data) => (data ? data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null);

/**
 * O ponto ao lado dos títulos: verde piscando enquanto o dado é de agora, âmbar e PARADO quando a
 * última atualização falhou.
 *
 * Antes ele piscava sempre. Numa TV sem ninguém por perto, um quadro que não atualiza há uma hora
 * continuava dizendo "ao vivo" — o sinal mentia justamente quando alguém precisava dele.
 */
const PontoAoVivo = ({ desatualizado = false, atraso = '0s' }) => (
  <span
    className={`inline-block h-2 w-2 shrink-0 rounded-full ${desatualizado ? 'bg-amber-400' : 'bg-emerald-400'}`}
    style={desatualizado ? undefined : { animation: `live-blink 1.6s ease-in-out infinite ${atraso}` }}
    role="img"
    aria-label={desatualizado ? 'Dados desatualizados' : 'Dados ao vivo'}
  />
);

/** De quando é o número na tela. Sempre visível: balão não existe em TV. */
const EstadoDoDado = ({ hora, desatualizado }) => {
  if (!hora) return null;

  return (
    <span className={desatualizado ? 'font-semibold text-amber-300' : undefined}>
      {desatualizado ? `Sem atualizar desde ${hora}` : `Atualizado às ${hora}`}
    </span>
  );
};

/**
 * Um valor do cabeçalho da TV: rótulo de 20 px e número de 48 px.
 *
 * São os mesmos cards do cabeçalho de mesa, no tamanho de quem lê do outro lado da sala. Meta
 * batida muda o CARD, e não só o número: é o estado que fica na tela depois da comemoração.
 */
const ValorDaTv = ({ icone: Icone, rotulo, valor, tom = 'neutro' }) => {
  const tons = {
    neutro: { caixa: 'border-white/[0.1] bg-white/[0.055]', rotulo: 'text-white/70', icone: 'text-[color:var(--accent)]' },
    meta: { caixa: 'border-[color:var(--status-good)]/50 bg-[color:var(--status-good)]/[0.10]', rotulo: 'text-[color:var(--status-good)]', icone: 'text-[color:var(--status-good)]' },
    supermeta: { caixa: 'border-white/[0.1] bg-white/[0.055]', rotulo: 'text-white/70', icone: 'text-amber-300' },
    supermetaBatida: { caixa: 'border-amber-300/60 bg-amber-300/[0.10]', rotulo: 'text-amber-200', icone: 'text-amber-300' },
  }[tom];

  return (
    <div className={`min-w-[200px] rounded-2xl border px-5 py-3 ${tons.caixa}`}>
      <p className={`flex items-center space-x-2 text-xl font-semibold ${tons.rotulo}`}>
        <Icone className={`h-5 w-5 shrink-0 ${tons.icone}`} aria-hidden="true" />
        {/* Num <span>: `space-x` põe a margem em ELEMENTOS, e texto solto ficaria colado no ícone. */}
        <span>{rotulo}</span>
      </p>
      <p className="mt-1.5 text-5xl font-black leading-none tabular-nums text-white">{numero(valor)}</p>
    </div>
  );
};

/**
 * O cabeçalho da TV: o título e os três números do período — vendido, meta e supermeta.
 *
 * Houve uma versão em que o progresso da equipe era uma barra atravessando a tela ("442 de 475,
 * faltam 33"). Quem usa o painel preferiu os valores soltos: o progresso que interessa é o de cada
 * vendedor, e ele já está na barra de cada linha.
 */
const CabecalhoDaTv = ({ empresa, periodo, vendas, meta, superMeta, hora, desatualizado }) => {
  const temMeta = Number(meta) > 0;
  const temSuper = Number(superMeta) > 0;
  const batida = temMeta && vendas >= meta;
  const superBatida = temSuper && vendas >= superMeta;

  return (
    <header className="mb-4 flex shrink-0 items-end justify-between space-x-8">
      <div className="min-w-0">
        <h2 className="truncate text-5xl font-black leading-tight text-white">Ranking de Vendedores</h2>
        <p className="mt-1 flex items-center space-x-3 text-xl font-semibold text-white/70" role="status">
          <span className="truncate">{empresa} · {periodo}</span>
          <PontoAoVivo desatualizado={desatualizado} />
          <EstadoDoDado hora={hora} desatualizado={desatualizado} />
        </p>
      </div>
      <div className="flex shrink-0 space-x-3">
        <ValorDaTv icone={Target} rotulo="Vendas" valor={vendas} />
        {temMeta && (
          <ValorDaTv icone={Target} rotulo={batida ? 'Meta batida' : 'Meta total'} valor={meta} tom={batida ? 'meta' : 'neutro'} />
        )}
        {temSuper && (
          <ValorDaTv
            icone={Trophy}
            rotulo={superBatida ? 'Supermeta batida' : 'Supermeta'}
            valor={superMeta}
            tom={superBatida ? 'supermetaBatida' : 'supermeta'}
          />
        )}
      </div>
    </header>
  );
};

const pessoaDaCena = (vendedor, vendas) => ({
  nome: nomeParaCena(vendedor.nome_exibicao || vendedor.nome),
  foto: vendedor.foto_url || '',
  pontos: vendedor.foto_pontos || undefined,
  cor: corDoVendedor(vendedor.id),
  vendas,
});

const getPositionBadgeClass = (index, isFullscreen) => {
  const base = isFullscreen
    ? 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-base font-black tabular-nums'
    : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-black tabular-nums';
  if (index === 0) return `${base} bg-amber-300 text-slate-950 shadow-[0_0_18px_rgba(251,191,36,0.45)]`;
  if (index === 1) return `${base} bg-slate-300 text-slate-900`;
  if (index === 2) return `${base} bg-amber-700/80 text-white`;
  return `${base} bg-white text-slate-950`;
};

const formatarData = (data) => {
  if (!data) return '';

  const [ano, mes, dia] = data.split('-');
  if (!ano || !mes || !dia) return data;

  return `${dia}/${mes}/${ano}`;
};

const getPeriodo = (filtroData) => {
  if (!filtroData?.start || !filtroData?.end) return 'Período selecionado';
  return `${formatarData(filtroData.start)} até ${formatarData(filtroData.end)}`;
};

const normalizarVendedor = (vendedor, index) => {
  const nome = vendedor?.nome || vendedor?.vendedor || 'Vendedor não informado';
  const contratos = Number(vendedor?.contratos ?? vendedor?.total_contratos ?? 0);

  const metaVendas = vendedor?.meta_vendas;
  const metaNumerica = metaVendas !== undefined && metaVendas !== null ? Number(metaVendas) : null;

  return {
    id: vendedor?.id || vendedor?.id_vendedor || vendedor?.vendedor_id || `${nome}-${index}`,
    nome,
    nome_exibicao: nomeDeExibicao(nome),
    contratos,
    meta_vendas: metaNumerica,
    percentual_meta: metaNumerica > 0 ? Number(vendedor?.percentual_meta ?? ((contratos / metaNumerica) * 100)) : null,
    saldo_meta: metaNumerica !== null ? Number(vendedor?.saldo_meta ?? (metaNumerica - contratos)) : null,
    tem_meta: Boolean(vendedor?.tem_meta || metaNumerica !== null),
    // Campos aditivos do regime da régua. Este normalizador RECONSTRÓI o objeto a partir de uma
    // whitelist, então campo novo do backend some antes de chegar à tela se não for listado aqui.
    fonte_meta: vendedor?.fonte_meta || null,
    fora_da_equipe: Boolean(vendedor?.fora_da_equipe),
    tipo_participante: vendedor?.tipo_participante || null,
    foto_url: vendedor?.foto_url || null,
    foto_pontos: vendedor?.foto_pontos || null,
    tem_foto: Boolean(vendedor?.tem_foto || vendedor?.foto_url),
    empresas: Array.isArray(vendedor?.empresas) ? vendedor.empresas : [],
  };
};

/**
 * Nome sem o sufixo de canal do cadastro IXC.
 *
 * A base nomeia o time como `FULANA VEND-INT`, `FULANA LOJA`, `Fulano -ALQ  PAP`. O sufixo é
 * convenção de cadastro, não parte do nome da pessoa, e ocupa metade do card do pódio.
 *
 * Remove só tokens do FIM, um a um, e nunca o nome inteiro: "IA vendas" continua "IA vendas"
 * (`vendas` não é `vend`), e um nome que fosse só sufixo é devolvido como está.
 */
const SUFIXOS_DE_CANAL = new Set(['vend', 'vendedor', 'vend-int', 'int', 'interno', 'ext', 'externo', 'loja', 'pap', 'alq']);

export const nomeDeExibicao = (nome = '') => {
  const tokens = String(nome).trim().split(/\s+/).filter(Boolean);

  while (tokens.length > 1) {
    const ultimo = tokens[tokens.length - 1].replace(/^[-–—]+|[-–—]+$/g, '').toLowerCase();
    if (!SUFIXOS_DE_CANAL.has(ultimo)) break;
    tokens.pop();
  }

  return tokens.join(' ') || String(nome);
};

/**
 * Barra de alcance da meta.
 *
 * Antes a barra media `contratos / maiorQuantidade` — proporção em relação à LÍDER — e ficava logo
 * abaixo de "Meta 210 · 28%". A primeira colocada aparecia com a barra cheia ao lado de 28%, o que
 * lê como meta atingida. Agora a barra é a meta: o trilho é 100%, e o preenchimento é o atingimento.
 *
 * A cor não usa vermelho de propósito. O período pode estar no meio do mês, e 28% no dia 8 não é
 * problema nenhum — pintar de vermelho julgaria um mês incompleto.
 */
const ProgressoDaMeta = ({ percentual, meta, contratos, compacto = false }) => {
  const pct = Math.max(0, Number(percentual) || 0);
  const largura = pct === 0 ? 0 : Math.max(3, Math.min(100, pct));
  const atingiu = pct >= 100;
  const cor = atingiu ? 'var(--status-good)' : pct >= 70 ? 'var(--status-warn)' : 'var(--accent)';

  return (
    <div className={compacto ? 'mt-2' : 'mt-3'}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[11px] font-medium text-white/[0.55]" title={atingiu ? `Meta batida: ${numero(contratos)} de ${numero(meta)}` : undefined}>
          {numero(contratos)} de {numero(meta)}
        </span>
        {/* Meta batida é ESTADO: ícone de confirmação mais cor, nunca um chip de texto — o chip
            disputava a linha com "313 de 188" e truncava o número que ela existe para mostrar.
            Cor sozinha não chega a quem não a distingue, então o estado também vai por escrito ao
            leitor de tela e ao `title`. */}
        <span className="flex shrink-0 items-center gap-1 text-xs font-bold tabular-nums" style={{ color: cor }}>
          {atingiu && <><Check className="h-3.5 w-3.5" aria-hidden="true"/><span className="sr-only">Meta batida. </span></>}
          {Math.round(pct)}%
        </span>
      </div>
      <div
        className={`${compacto ? 'mt-1 h-1.5' : 'mt-1.5 h-2'} overflow-hidden rounded-full bg-white/[0.12]`}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Alcance da meta: ${numero(contratos)} de ${numero(meta)}`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${largura}%`, backgroundColor: cor }}
        />
      </div>
    </div>
  );
};

/**
 * Retrato do que a tela mostra agora, para comparar com o ciclo anterior.
 *
 * A chave carrega período e empresa: trocar o filtro de mês não pode disparar celebração, porque
 * ali nada foi conquistado — só mudou o recorte.
 */
export const instantaneoDoRanking = ({ chave, totalVendas, metaTotal, superMetaTotal = null, ranking = [] }) => ({
  chave,
  vendas: Number(totalVendas || 0),
  meta: Number(metaTotal || 0),
  // Nula quando ninguém configurou. Coagir para zero faria a supermeta nascer batida.
  superMeta: superMetaTotal === null || superMetaTotal === undefined ? null : Number(superMetaTotal),
  vendedores: ranking
    .filter((vendedor) => vendedor.tem_meta && Number(vendedor.meta_vendas) > 0)
    .map((vendedor) => ({
      id: String(vendedor.id),
      nome: vendedor.nome_exibicao || vendedor.nome,
      percentual: Number(vendedor.percentual_meta || 0),
    })),
});

/**
 * O que foi conquistado ENTRE duas atualizações.
 *
 * Só a virada conta. Abrir o painel com a meta já batida não celebra nada: a conquista foi de
 * outro dia, e uma TV que comemora a cada recarga vira ruído em uma tarde.
 */
export const detectarMetasBatidas = (anterior, atual) => {
  const vazio = { total: false, supermeta: false, vendedores: [] };
  if (!anterior || !atual || anterior.chave !== atual.chave) return vazio;

  const total = atual.meta > 0 && anterior.meta > 0 && anterior.vendas < anterior.meta && atual.vendas >= atual.meta;
  const supermeta = Boolean(atual.superMeta) && Boolean(anterior.superMeta)
    && anterior.vendas < anterior.superMeta && atual.vendas >= atual.superMeta;
  const antes = new Map(anterior.vendedores.map((vendedor) => [vendedor.id, vendedor]));
  const vendedores = atual.vendedores
    .filter((vendedor) => {
      const anteriorDele = antes.get(vendedor.id);
      return Boolean(anteriorDele) && anteriorDele.percentual < 100 && vendedor.percentual >= 100;
    })
    .map((vendedor) => vendedor.nome);

  // Bater as duas no mesmo ciclo celebra só a supermeta: ela contém a outra, e dois confetes
  // simultâneos dizem a mesma coisa duas vezes.
  return { total: total && !supermeta, supermeta, vendedores };
};

const getIniciais = (nome = '') => {
  const partes = nome
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (partes.length === 0) return 'VI';

  return partes.map((parte) => parte[0]).join('').toUpperCase();
};

/**
 * Quem bateu a meta ganha um troféu no avatar.
 *
 * Mora no avatar, e não na linha de texto, porque ali ele pode ser GRANDE sem empurrar nada: a
 * tentativa anterior — um chip "META BATIDA" ao lado do percentual — truncava "313 de 188", o
 * número que a linha existe para mostrar. Num painel visto do outro lado da sala, o selo é o que
 * se lê primeiro; o percentual continua respondendo "quanto".
 *
 * `aria-hidden`: o estado já vai por escrito ao leitor de tela em `ProgressoDaMeta`, e dois
 * anúncios para o mesmo fato viram ruído.
 */
const SeloMetaBatida = ({ isFullscreen = false }) => (
  <span
    aria-hidden="true"
    title="Meta batida"
    className={`absolute -right-1.5 -top-1.5 grid place-items-center rounded-full bg-amber-300 text-slate-950 shadow-[0_6px_18px_rgba(251,191,36,0.45)] ring-2 ring-slate-950 ${isFullscreen ? 'h-8 w-8' : 'h-7 w-7'}`}
  >
    <Trophy className={isFullscreen ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
  </span>
);

const metaBatidaDoVendedor = (vendedor) => Boolean(vendedor?.tem_meta) && Number(vendedor?.percentual_meta || 0) >= 100;

const VendedorAvatar = ({ vendedor, className = '', textClassName = '' }) => {
  if (vendedor.foto_url) {
    return (
      <img
        src={vendedor.foto_url}
        alt={`Foto de ${vendedor.nome}`}
        className={`shrink-0 rounded-2xl object-cover ring-1 ring-white/[0.15] ${className}`}
      />
    );
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--accent),rgba(250,204,21,0.88))] font-black text-white shadow-[0_12px_32px_rgba(2,6,23,0.26)] ring-1 ring-white/[0.15] ${className} ${textClassName}`}
      aria-label={`Avatar de ${vendedor.nome}`}
    >
      {getIniciais(vendedor.nome)}
    </span>
  );
};

const PodiumCard = ({ vendedor, posicao, destaque, isFullscreen = false }) => {
  const Icon = posicao === 1 ? Crown : Medal;
  const alignmentClass = isFullscreen
    ? destaque
      ? 'lg:order-2 lg:col-start-2 lg:h-[clamp(360px,46dvh,420px)] xl:h-[clamp(380px,48dvh,440px)]'
      : posicao === 2
        ? 'lg:order-3 lg:h-[clamp(330px,42dvh,380px)] xl:h-[clamp(350px,44dvh,400px)]'
        : 'lg:order-1 lg:h-[clamp(310px,39dvh,360px)] xl:h-[clamp(330px,41dvh,380px)]'
    : destaque
      ? 'lg:order-2 lg:col-start-2 lg:-mt-8 lg:h-[340px]'
      : posicao === 2
        ? 'lg:order-3 lg:mt-10 lg:h-[310px]'
        : 'lg:order-1 lg:mt-14 lg:h-[292px]';
  const glowClass = destaque
    ? 'border-amber-300/70 bg-amber-300/[0.08] shadow-[0_0_60px_rgba(251,191,36,0.22)]'
    : 'border-white/[0.18] bg-white/[0.055]';
  const cardScaleClass = isFullscreen
    ? 'min-h-[350px] rounded-[28px] p-5 xl:p-6'
    : 'min-h-[270px] rounded-2xl p-5';
  const avatarClass = isFullscreen
    ? destaque
      ? 'h-28 w-28 rounded-[32px] ring-2 ring-amber-300/80 xl:h-32 xl:w-32'
      : 'h-[92px] w-[92px] rounded-[28px] ring-1 ring-white/25 xl:h-24 xl:w-24'
    : destaque
      ? 'h-[88px] w-[88px] rounded-[26px] ring-2 ring-amber-300/80'
      : 'h-[74px] w-[74px] rounded-3xl ring-1 ring-white/25';
  const avatarTextClass = isFullscreen
    ? destaque ? 'text-3xl xl:text-4xl' : 'text-xl xl:text-2xl'
    : destaque ? 'text-xl' : 'text-lg';
  const badgeClass = isFullscreen
    ? `absolute -bottom-3 -right-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl border shadow-xl ${destaque ? 'border-amber-200/70 bg-amber-300 text-slate-950 shadow-amber-500/25' : 'border-white/15 bg-white/[0.16] text-white'}`
    : `absolute -bottom-2 -right-2 inline-flex h-9 w-9 items-center justify-center rounded-xl border shadow-lg ${destaque ? 'border-amber-200/70 bg-amber-300 text-slate-950 shadow-amber-500/20' : 'border-white/15 bg-white/[0.16] text-white'}`;
  const positionClass = isFullscreen
    ? `mx-auto mb-3 flex items-center justify-center rounded-full border ${destaque ? 'h-[52px] w-[52px] border-amber-300 bg-amber-300/[0.16]' : 'h-11 w-11 border-white/[0.32] bg-white/10'}`
    : `mx-auto mb-2.5 flex items-center justify-center rounded-full border ${destaque ? 'h-11 w-11 border-amber-300 bg-amber-300/[0.16]' : 'h-9 w-9 border-white/[0.32] bg-white/10'}`;

  return (
    <article
      className={`group relative border text-white transition duration-300 hover:-translate-y-1 lg:min-h-0 ${cardScaleClass} ${alignmentClass} ${glowClass}`}
    >
      <div className="absolute top-0 right-0 bottom-0 left-0 rounded-[inherit] bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.16),transparent_46%)] opacity-80" />
      <div className="relative flex h-full flex-col items-center justify-between text-center">
        <div className="relative">
          {destaque && (
            <span
              className="absolute top-0 right-0 bottom-0 left-0 rounded-[34px] border-2 border-amber-300/60"
              style={{ animation: 'pulse-ring 2.2s ease-out infinite' }}
              aria-hidden="true"
            />
          )}
          <VendedorAvatar
            vendedor={vendedor}
            className={`${avatarClass} border border-white/[0.16] shadow-[0_18px_45px_rgba(0,0,0,0.35)]`}
            textClassName={avatarTextClass}
          />
          {/* Canto superior ESQUERDO: a coroa e a medalha já ocupam o inferior direito. */}
          {metaBatidaDoVendedor(vendedor) && (
            <span aria-hidden="true" title="Meta batida" className={`absolute -left-2 -top-2 grid place-items-center rounded-full bg-amber-300 text-slate-950 shadow-[0_6px_18px_rgba(251,191,36,0.45)] ring-2 ring-slate-950 ${isFullscreen ? 'h-10 w-10' : 'h-8 w-8'}`}>
              <Trophy className={isFullscreen ? 'h-5 w-5' : 'h-4 w-4'} />
            </span>
          )}
          <span className={badgeClass}>
            <Icon className={isFullscreen ? 'h-6 w-6' : 'h-5 w-5'} />
          </span>
        </div>

        <div className={isFullscreen ? 'my-2.5 w-full min-w-0 px-1' : 'my-3 w-full min-w-0 px-1'}>
          <div className={positionClass}>
            <span className={isFullscreen ? 'text-xl font-black' : 'text-base font-black'}>{posicao}</span>
          </div>
          <h3
            className={`mx-auto max-w-full overflow-hidden font-black leading-tight [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] ${isFullscreen ? 'text-lg xl:text-xl' : 'text-base'}`}
            title={vendedor.nome}
          >
            {vendedor.nome_exibicao || vendedor.nome}
          </h3>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/[0.52]">Total de vendas</p>
          {vendedor.tem_meta && (
            <ProgressoDaMeta
              percentual={vendedor.percentual_meta}
              meta={vendedor.meta_vendas}
              contratos={vendedor.contratos}
              compacto
            />
          )}
          {!vendedor.tem_meta && vendedor.fora_da_equipe && (
            <p className="mt-2 truncate text-xs font-medium text-[color:var(--status-warn)]" title="Vendeu no período mas não foi cadastrado na equipe da competência. Estas vendas bloqueiam o fechamento da comissão.">
              Fora da equipe da competência
            </p>
          )}
        </div>

        <div>
          <p className={`font-black leading-none tabular-nums ${destaque ? (isFullscreen ? 'text-5xl text-amber-300' : 'text-3xl text-amber-300') : (isFullscreen ? 'text-3xl text-white xl:text-4xl' : 'text-2xl text-white')}`}>
            {numero(vendedor.contratos)}
          </p>
        </div>
      </div>
    </article>
  );
};

/**
 * A barra de cada vendedor na TV. Mesma regra de cor da barra de mesa (`ProgressoDaMeta`): meta
 * batida, perto dela, ou a caminho — e nunca vermelho, porque o período pode estar no começo.
 */
const BarraDaTv = ({ vendedor, className = '' }) => {
  const pct = Number(vendedor.percentual_meta) || 0;
  const cor = pct >= 100 ? 'var(--status-good)' : pct >= 70 ? 'var(--status-warn)' : 'var(--accent)';

  return (
    <span className={`barra-da-meta block h-3.5 min-w-0 shrink-0 overflow-hidden rounded-full bg-white/[0.14] ${className}`} aria-hidden="true">
      <span className="block h-full rounded-full" style={{ width: `${pct <= 0 ? 0 : Math.max(3, Math.min(100, pct))}%`, backgroundColor: cor }} />
    </span>
  );
};

/** Ouro, prata e bronze — os mesmos das medalhas das cenas (`cenasDoRanking.js`). */
const CORES_DO_PODIO = ['#F2C14E', '#C9D2DE', '#D98A4E'];

/**
 * A foto flexível depende de `aspect-ratio`, que só existe do Chromium 88 em diante. O navegador
 * nativo das TVs é mais velho (o da LG com webOS 6 é o 79), e nele a foto sumia: sem a proporção, a
 * caixa ficava com largura zero. Onde não há suporte, a foto usa um tamanho FIXO por posição
 * (`fixas`, abaixo), calculado para caber no menor card de cada fileira.
 */
const FOTO_FLEXIVEL = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('aspect-ratio', '1');

/**
 * Tamanho do texto do card conforme quantos dividem a fileira. `foto` é o teto da foto flexível;
 * `fixas` são os lados da foto, em px, para o 1º, o 2º, o 3º e os demais, onde ela não pode ser
 * flexível.
 */
const MEDIDAS_DO_CARD = {
  folgado: { nome: 'text-[34px]', numero: 'text-[88px]', foto: 'max-h-[200px]', fixas: [176, 150, 132, 114], apoio: 'text-2xl', curto: false },
  medio: { nome: 'text-[30px]', numero: 'text-[72px]', foto: 'max-h-[160px]', fixas: [150, 142, 126, 110], apoio: 'text-2xl', curto: false },
  // Com seis cards a linha de apoio não cabe inteira: perde o "da meta" e um ponto de tamanho,
  // em vez de quebrar em duas linhas e empurrar a foto.
  justo: { nome: 'text-[28px]', numero: 'text-[60px]', foto: 'max-h-[128px]', fixas: [122, 122, 116, 104], apoio: 'text-xl', curto: true },
};

/**
 * Um vendedor na TV: o rosto, o nome, o número e quanto ele fez da meta.
 *
 * A TV mostra só a equipe INTERNA, que são poucos — então cada um ganha um card inteiro, num pódio
 * simétrico (`ordemDoPodioSimetrico`), em vez de três no pódio e o resto numa lista. A lista
 * existiu em três versões e nenhuma serviu: linha estreita com iniciais no lugar do rosto lia como
 * "os outros".
 *
 * O desenho segue duas ideias, de duas referências lidas antes de mexer aqui:
 *
 * - **a cor vem do conteúdo, não da moldura.** O card é uma superfície lisa, sem borda e sem
 *   brilho; o que tem cor é a foto, o anel dela e o número. O anel e a faixa do topo dizem a
 *   posição em ouro, prata e bronze — as mesmas cores das medalhas das cenas, e é o rosto redondo
 *   com anel que faz o card e a cena parecerem da mesma tela;
 * - **peso em dois degraus.** Nome e número em peso máximo, e tudo o que explica ("de 159 · 36%
 *   da meta") em peso médio e tom mais baixo. Três pesos parecidos lado a lado não se separam a
 *   seis metros.
 *
 * A FOTO É FLEXÍVEL: ocupa a altura que sobrar no card, até um teto. É isso que deixa o card do
 * líder, mais alto, com a foto maior, e garante que nada transborda quando a pista tem seis raias.
 * Nada aqui tem menos de 20 px na tela de 1080.
 */
const CardDaTv = ({ vendedor, posicao, lugar = 0, medida = 'folgado' }) => {
  const lider = posicao === 1;
  const batida = metaBatidaDoVendedor(vendedor);
  const m = MEDIDAS_DO_CARD[medida];
  const cor = CORES_DO_PODIO[posicao - 1];
  // Do 4º em diante a altura para de cair: abaixo disso sobraria pouco para a foto.
  const altura = ['h-full', 'h-[94%]', 'h-[90%]'][posicao - 1] || 'h-[86%]';
  const pct = Math.round(Number(vendedor.percentual_meta) || 0);
  const ladoDaFoto = m.fixas[Math.min(posicao, m.fixas.length) - 1];

  return (
    <li
      style={{ order: lugar }}
      // Espaço entre os itens por margem, e não por `gap`: em flex ele só existe do Chromium 84 em
      // diante, e na TV os itens ficavam colados.
      className={`relative flex min-h-0 w-full min-w-0 max-w-[560px] flex-col items-center justify-center overflow-hidden rounded-[24px] px-5 pb-5 pt-7 text-center text-white ${altura} ${lider ? 'bg-amber-300/[0.10]' : 'bg-white/[0.06]'}`}
    >
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: cor || 'rgba(255,255,255,0.16)' }} />
      <div
        className={`relative ${FOTO_FLEXIVEL ? `aspect-square min-h-0 flex-1 basis-0 ${m.foto}` : 'shrink-0'}`}
        style={FOTO_FLEXIVEL ? undefined : { width: ladoDaFoto, height: ladoDaFoto }}
      >
        <div className="h-full w-full rounded-full p-1.5" style={{ backgroundColor: cor || 'rgba(255,255,255,0.28)' }}>
          <VendedorAvatar vendedor={vendedor} className="h-full w-full rounded-full ring-0" textClassName="text-4xl" />
        </div>
        {lider && (
          <Crown aria-hidden="true" className="absolute -top-5 right-0 h-12 w-12 rotate-[18deg] fill-amber-300 text-amber-300 drop-shadow-[0_3px_6px_rgba(2,6,23,0.7)]" />
        )}
        <span
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-0.5 text-2xl font-black tabular-nums text-slate-950 shadow-[0_4px_12px_rgba(2,6,23,0.5)]"
          style={{ backgroundColor: cor || '#FFFFFF' }}
        >
          {posicao}º<span className="sr-only"> lugar</span>
        </span>
      </div>
      <h3 className={`mt-6 w-full shrink-0 truncate font-black leading-tight ${m.nome}`} title={vendedor.nome}>
        {nomeParaCena(vendedor.nome_exibicao || vendedor.nome)}
      </h3>
      <p className={`mt-2 shrink-0 font-black leading-none tabular-nums ${m.numero}`} style={{ color: lider ? CORES_DO_PODIO[0] : '#FFFFFF' }}>
        {numero(vendedor.contratos)}
      </p>
      {vendedor.tem_meta ? (
        <>
          <BarraDaTv vendedor={vendedor} className="mt-2.5 w-full" />
          <p className={`mt-2 flex shrink-0 items-center space-x-2 whitespace-nowrap font-medium leading-tight tabular-nums ${m.apoio} ${batida ? 'text-[color:var(--status-good)]' : 'text-white/70'}`}>
            {batida && <Trophy className="h-6 w-6 shrink-0 text-amber-300" aria-hidden="true" />}
            <span>{batida ? `meta batida · ${pct}%` : `de ${numero(vendedor.meta_vendas)} · ${pct}%${m.curto ? '' : ' da meta'}`}</span>
          </p>
        </>
      ) : (
        <p className={`mt-2 shrink-0 whitespace-nowrap font-medium leading-tight text-white/70 ${m.apoio}`}>vendas no período</p>
      )}
    </li>
  );
};

/** O título de um bloco da TV: uma linha, sem caixa em volta, com um fio até o que vem à direita. */
const TituloDaTv = ({ icone: Icone, titulo, desatualizado, children }) => (
  <div className="mb-3 flex shrink-0 items-center space-x-3">
    <Icone className="h-7 w-7 shrink-0 text-amber-300" aria-hidden="true" />
    <h3 className="text-2xl font-black text-white">{titulo}</h3>
    <PontoAoVivo desatualizado={desatualizado} />
    <span className="h-px min-w-0 flex-1 bg-white/[0.14]" aria-hidden="true" />
    {children && <p className="shrink-0 text-xl font-medium text-white/70">{children}</p>}
  </div>
);

/**
 * Em que lugar da fileira fica cada posição: o 1º no centro, e os outros alternando para os dois
 * lados — 2º à esquerda dele, 3º à direita, 4º mais à esquerda, 5º mais à direita.
 *
 * A primeira versão punha todo mundo da esquerda para a direita, cada card mais baixo que o
 * anterior. Virava uma escada descendo, pesada de um lado só, e o líder ficava no canto. Assim é um
 * pódio: o olho cai no meio, e a altura cai por igual para os dois lados.
 *
 * Devolve as posições na ordem em que aparecem na tela: com cinco, `[4, 2, 1, 3, 5]`.
 */
export const ordemDoPodioSimetrico = (total) => {
  const ordem = [];

  for (let posicao = 1; posicao <= total; posicao += 1) {
    if (posicao % 2 === 0) ordem.unshift(posicao);
    else ordem.push(posicao);
  }

  return ordem;
};

/**
 * Quem aparece na TV.
 *
 * Só os vendedores INTERNOS, quando a API informa o segmento (`tipo_participante`, sob a régua): a
 * TV fica na sala deles, e loja e parceiros com uma venda no mês só enchiam a tela. Sem essa
 * informação — período legado — entra todo mundo, como antes.
 */
export const vendedoresDaTv = (ranking = []) => {
  const internos = ranking.filter((vendedor) => vendedor.tipo_participante === 'interno');

  return internos.length > 0 ? { somenteInternos: true, vendedores: internos } : { somenteInternos: false, vendedores: ranking };
};

const RankingSkeleton = () => (
  <div className="space-y-6">
    <div className="h-28 animate-pulse rounded-3xl border border-white/10 bg-white/[0.055]" />
    <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="h-[420px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.055]" />
      <div className="h-[420px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.055]" />
    </div>
  </div>
);

const RankingComercialTab = ({
  filtroData: filtroPedido,
  companyContext,
  fullscreen = false,
}) => {
  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVAL_DEFAULT_MS);
  const [lastPollingRefresh, setLastPollingRefresh] = useState(null);
  const [refreshFeedback, setRefreshFeedback] = useState(false);
  const [falhasSeguidas, setFalhasSeguidas] = useState(0);
  const [isFakeFullscreen, setIsFakeFullscreen] = useState(false);
  const [vendedorFotoEmEdicao, setVendedorFotoEmEdicao] = useState(null);
  const [celebracoes, setCelebracoes] = useState([]);
  const [filaDeCenas, setFilaDeCenas] = useState([]);
  const [vendasNaPista, setVendasNaPista] = useState(null);
  const [metasNaPista, setMetasNaPista] = useState(null);
  /**
   * As cenas têm dois interruptores, um por tela.
   *
   * Na MESA a escolha fica guardada no navegador: quem não quer cena por cima do trabalho desliga uma
   * vez. Na APRESENTAÇÃO elas começam sempre ligadas, e desligar vale só até sair dela. Era um
   * interruptor só, guardado — e uma TV passou a tarde sem cena nenhuma porque alguém tinha
   * desligado as cenas naquele navegador no dia anterior, sem nada na tela que avisasse.
   */
  const [cenasDaMesa, setCenasDaMesa] = useState(() => lerPreferencia(CHAVE_DAS_CENAS));
  const [cenasDaApresentacao, setCenasDaApresentacao] = useState(true);
  const naApresentacao = isFakeFullscreen || fullscreen;
  const cenasLigadas = naApresentacao ? cenasDaApresentacao : cenasDaMesa;
  useEffect(() => {
    if (!naApresentacao) setCenasDaApresentacao(true);
  }, [naApresentacao]);
  const [somLigado, setSomLigado] = useState(() => lerPreferencia(CHAVE_DO_SOM));
  const [hoje, setHoje] = useState(() => diaDeHoje());
  const filtroData = useMemo(() => periodoQueAcompanhaODia(filtroPedido, hoje), [filtroPedido, hoje]);
  const [alturaDaJanela, setAlturaDaJanela] = useState(() => window.innerHeight);
  const [ocioso, setOcioso] = useState(false);
  const refreshFeedbackTimer = useRef(null);
  const cenaDeTeste = useRef(0);
  const { hasPermission } = usePermissions();
  /**
   * As vendas de HOJE, numa segunda instância do hook, para a corrida do dia aparecer embaixo do
   * ranking do período. É a mesma rota, com início e fim iguais.
   *
   * Memoizado: o hook recarrega quando a IDENTIDADE do filtro muda, e um objeto novo a cada render
   * viraria uma requisição por render.
   *
   * Declarada ANTES da principal de propósito: os efeitos rodam na ordem dos hooks, e a carga do
   * período — a que a tela espera para sair do esqueleto — continua sendo a última a ser pedida.
   */
  const filtroDoDia = useMemo(() => ({ start: hoje, end: hoje }), [hoje]);
  const {
    ranking: rankingDoDiaApi,
    loading: carregandoODia,
    error: erroDoDia,
    chaveDosDados: chaveDosDadosDoDia,
    recarregar: recarregarODia,
    metaDiariaInterna: metaDiariaDoDia,
  } = useComercialRanking(filtroDoDia, companyContext);
  const {
    ranking: rankingApi,
    top3,
    totalVendas: totalVendasCarregado,
    metaTotal,
    superMetaTotal,
    marcosDeMeta,
    loading,
    error,
    ultimaAtualizacao,
    chaveDosDados,
    recarregar,
  } = useComercialRanking(filtroData, companyContext);
  // Entre a troca do filtro de período e a resposta, o que está em memória é do período ANTERIOR.
  // Exibi-lo sob as datas novas seria número errado sem erro nenhum.
  const dadosDoRecorte = chaveDosDados === chaveDoRecorte(filtroData);
  const aguardandoRecorte = loading && !dadosDoRecorte;
  const ranking = useMemo(
    () => (dadosDoRecorte ? rankingApi.map(normalizarVendedor) : []),
    [rankingApi, dadosDoRecorte],
  );
  const totalVendas = dadosDoRecorte ? totalVendasCarregado : 0;
  const topTres = dadosDoRecorte && top3.length > 0
    ? top3.map(normalizarVendedor)
    : ranking.slice(0, 3);
  const maiorQuantidade = Math.max(...ranking.map((vendedor) => vendedor.contratos), 1);
  const empresaAtual = companyContext?.isGroup || companyContext?.isConsolidated
    ? `${companyContext?.companies?.length || 0} empresas`
    : companyContext?.companies?.[0]?.name || 'Empresa atual';
  const empresaEdicaoFotos = vendedorFotoEmEdicao?.empresaFoto || companyContext?.companies?.[0]?.name || 'MOV Fibra';
  const empresaDasCenas = companyContext?.companies?.[0]?.slug || companyContext?.companies?.[0]?.name || '';
  const podeEditarFotos = hasPermission('comercial.metas.edit');

  // A corrida de hoje: as raias saem dos internos do período, e o quanto cada kart andou, das
  // vendas do dia.
  const diaCarregado = chaveDosDadosDoDia === chaveDoRecorte(filtroDoDia);
  const rankingDoDia = useMemo(
    () => (diaCarregado ? rankingDoDiaApi.map(normalizarVendedor) : []),
    [diaCarregado, rankingDoDiaApi],
  );
  const corrida = useMemo(
    () => corredoresDeHoje(ranking, rankingDoDia, diaCarregado ? metaDiariaDoDia : null),
    [ranking, rankingDoDia, diaCarregado, metaDiariaDoDia],
  );
  const daTv = useMemo(() => vendedoresDaTv(ranking), [ranking]);
  // A pista aparece assim que há ranking, mesmo antes de as vendas do dia chegarem: esperar por
  // elas faria a TV trocar de layout sozinha um segundo depois de abrir.
  const mostrarCorrida = ranking.length > 0;
  const estadoDoDia = diaCarregado ? (erroDoDia ? 'desatualizado' : 'ok') : (erroDoDia ? 'indisponivel' : 'carregando');

  /**
   * BACKOFF PROGRESSIVO no erro.
   *
   * Antes, o polling insistia no mesmo ritmo mesmo depois de a origem falhar: com o IXC fora por
   * uma hora e o intervalo em 1 min, eram 60 tentativas fracassadas. Agora o intervalo dobra a cada
   * falha consecutiva, até o teto, e volta ao escolhido no primeiro sucesso.
   *
   * Dobrar em vez de parar é deliberado: esta tela é usada em modo apresentação, sem ninguém por
   * perto para clicar em "tentar novamente". Parar de vez exigiria intervenção humana justamente
   * quando não há humano. O banner mostra o intervalo em vigor para que a espera maior não pareça
   * travamento.
   */
  const intervaloEfetivo = useMemo(
    () => Math.min(pollingInterval * 2 ** falhasSeguidas, BACKOFF_TETO_MS),
    [pollingInterval, falhasSeguidas],
  );

  const triggerRefreshFeedback = useCallback(() => {
    setRefreshFeedback(true);
    if (refreshFeedbackTimer.current) {
      clearTimeout(refreshFeedbackTimer.current);
    }
    refreshFeedbackTimer.current = setTimeout(() => {
      setRefreshFeedback(false);
      refreshFeedbackTimer.current = null;
    }, MIN_REFRESH_FEEDBACK_MS);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      // A virada do dia zera a pista sozinha: uma TV ligada de um dia para o outro não pode
      // amanhecer mostrando a corrida de ontem como "hoje". Mudar `hoje` já dispara a carga nova.
      const diaAgora = diaDeHoje();
      if (diaAgora !== hoje) setHoje(diaAgora);

      triggerRefreshFeedback();
      // SEM `force`: o polling respeita o cache de 5 min da leitura direta. Forçar aqui invalidava
      // a geração do cache do ranking para TODA a empresa a cada ciclo — uma aba aberta bastava
      // para ninguém mais receber ranking cacheado. Quem quer o número agora usa o botão.
      // A corrida do dia vai junto, mas quem conta para o backoff é o ranking: é ele que a tela
      // promete, e a pista parada por um ciclo não justifica espaçar as consultas.
      const [ok] = await Promise.all([recarregar(), recarregarODia()]);
      setFalhasSeguidas((anteriores) => (ok ? 0 : anteriores + 1));
      setLastPollingRefresh(new Date());
    }, intervaloEfetivo);

    return () => clearInterval(intervalId);
  }, [intervaloEfetivo, recarregar, recarregarODia, triggerRefreshFeedback, hoje]);

  /**
   * As cenas tocam só em modo apresentação: é a TV da operação que elas existem para animar. Na
   * tela de quem está trabalhando, oito segundos de tela cheia com som a cada venda atrapalham — ali
   * vale o confete de sempre. Entrar em apresentação é um clique, e é ele que libera o áudio: sem
   * interação nenhuma, o navegador bloqueia som automático.
   */
  const emApresentacao = isFakeFullscreen || fullscreen;
  // O palco existe nos dois modos; o que muda é o que sobe nele. Em apresentação, tudo. Fora dela,
  // só meta batida — individual, da equipe e supermeta —, que é rara o bastante para merecer a
  // tela de quem está trabalhando.
  const palcoLigado = cenasLigadas && !prefereMenosMovimento();
  const cenasAtivas = emApresentacao && palcoLigado;

  useEffect(() => {
    if (cenasAtivas) prepararCenas(RECURSOS_DAS_CENAS, empresaDasCenas);
    // Trocar de modo ou desligar as cenas tira do ar a que estiver tocando e esvazia a fila: uma
    // ultrapassagem na fila da TV não tem por que tocar na mesa de quem acabou de sair dela.
    setFilaDeCenas([]);
  }, [cenasAtivas, palcoLigado, empresaDasCenas]);

  /**
   * A festa nasce da COMPARAÇÃO entre dois ciclos, não do dado em si.
   *
   * O polling desta tela roda sozinho a cada poucos minutos; quando o ciclo traz alguém passando
   * outro, cruzando uma faixa ou a equipe batendo a meta, é essa passagem — e só ela — que dispara
   * a cena. O primeiro retrato depois de abrir a tela ou trocar de período apenas estabelece a linha
   * de base.
   */
  const chaveDoPeriodo = `${chaveDoRecorte(filtroData)}|${(companyContext?.ids || []).join(',')}`;
  const chaveDoDia = `${hoje}|${(companyContext?.ids || []).join(',')}`;
  const retratoAnterior = useRef(null);
  // Lido na hora de tocar: ligar ou desligar o som não pode refazer a comparação dos retratos.
  const somAtual = useRef(somLigado);
  somAtual.current = somLigado;

  useEffect(() => {
    // Sem os dados do recorte pedido não há linha de base: fixá-la com o que sobrou do recorte
    // anterior faria a carga seguinte parecer uma virada de meta.
    if (loading || !dadosDoRecorte) return;
    // Período e dia chegam em respostas separadas. Comparar com um deles ainda no ciclo anterior
    // partiria a mesma venda em dois eventos — e a fila escolheria a cena errada.
    // Se as vendas de hoje não vieram por falha, a comparação segue só com o período: a pista fora
    // do ar não pode calar a comemoração de uma meta batida.
    if (carregandoODia || (!diaCarregado && !erroDoDia)) return;

    // Na TV a disputa é a que está na tela: só os internos. Uma ultrapassagem entre duas pessoas que
    // a tela não mostra, ou a meta de alguém de fora dela, não tem por que virar cena ali. O modo
    // entra na chave para a troca de mesa para TV só fixar a linha de base, sem comparar populações.
    const disputa = emApresentacao ? daTv.vendedores : ranking;
    const chaveDaDisputa = `${chaveDoPeriodo}|${emApresentacao ? 'tv' : 'mesa'}`;
    const anterior = retratoAnterior.current;
    const atual = {
      batidas: instantaneoDoRanking({ chave: chaveDoPeriodo, totalVendas, metaTotal, superMetaTotal, ranking }),
      periodo: retratoDaCorrida(chaveDaDisputa, disputa),
      metas: retratoDasMetas(chaveDaDisputa, disputa, marcosDeMeta),
      dia: diaCarregado
        ? retratoDaCorrida(chaveDoDia, corrida.classificacao.map((raia) => ({ id: raia.id, contratos: raia.hoje })))
        : null,
    };
    retratoAnterior.current = atual;

    const { total, supermeta, vendedores } = detectarMetasBatidas(anterior?.batidas, atual.batidas);
    const marca = Date.now();

    // Venda nova no dia: o kart de quem vendeu anda com um "+1" em cima. O SOM da venda só toca
    // quando o ciclo não tem cena nenhuma — a cena abre com a chamada dela, e os dois juntos se
    // atropelariam. Como as cenas, o som é da tela de apresentação.
    const vendasNovas = detectarVendasNovas(anterior?.dia, atual.dia);
    if (vendasNovas.length) {
      setVendasNaPista({
        marca,
        porId: vendasNovas.reduce((mapa, venda) => ({ ...mapa, [venda.id]: venda.quantas }), {}),
      });
    }
    // Bateu a meta ou a supermeta do dia: a pista comemora, e o som desse ciclo é o da meta, e não o
    // da venda — os dois juntos se atropelariam.
    const metasDoDia = detectarMetasDoDia(anterior?.dia, atual.dia, corrida.metaChegada, corrida.supermetaChegada);
    if (metasDoDia.length) {
      setMetasNaPista({
        marca,
        porId: metasDoDia.reduce((mapa, evento) => ({ ...mapa, [evento.id]: evento.tipo }), {}),
      });
    }
    const avisarVenda = () => {
      if (!emApresentacao || !somAtual.current) return;
      if (metasDoDia.length) tocarSomDoRanking(metasDoDia.some((evento) => evento.tipo === 'supermeta') ? CHAMADA_DA_SUPERMETA_DO_DIA : CHAMADA_DA_META_DO_DIA);
      else if (vendasNovas.length) tocarSomDoRanking(CHAMADA_DE_VENDA);
    };

    if (!palcoLigado) {
      avisarVenda();
      if (!total && !supermeta && vendedores.length === 0) return;

      setCelebracoes((atuais) => [
        ...atuais,
        ...(supermeta ? [{ id: `supermeta-${marca}`, tipo: 'supermeta', vendas: atual.batidas.vendas, meta: atual.batidas.superMeta }] : []),
        ...(total ? [{ id: `total-${marca}`, tipo: 'total', vendas: atual.batidas.vendas, meta: atual.batidas.meta }] : []),
        ...(vendedores.length ? [{ id: `vendedor-${marca}`, tipo: 'vendedor', nomes: vendedores }] : []),
      ]);
      return;
    }

    const marcos = detectarMarcos(anterior?.metas, atual.metas);
    const equipeBateu = supermeta ? 'supermeta' : total ? 'meta' : null;
    const fila = montarFilaDeCenas(emApresentacao
      ? {
        doDia: detectarUltrapassagens(anterior?.dia, atual.dia),
        doPeriodo: detectarUltrapassagens(anterior?.periodo, atual.periodo),
        marcos,
        equipe: equipeBateu,
      }
      : { marcos: marcos.filter((marco) => marco.bateuAMeta), equipe: equipeBateu });
    if (fila.length === 0) {
      avisarVenda();
      return;
    }

    const porId = new Map(ranking.map((vendedor) => [String(vendedor.id), vendedor]));
    const naCorrida = new Map(corrida.raias.map((raia) => [String(raia.id), raia]));
    const duracao = fila.length > CENAS_LONGAS_POR_CICLO ? 'curta' : 'longa';

    const cenas = fila.map((cena, indice) => {
      const base = { id: `${cena.tipo}-${marca}-${indice}`, chamada: chamadaDaCena(cena) };

      if (cena.tipo === 'ultrapassagem') {
        const doDia = cena.recorte === 'hoje';
        const quem = (id) => (doDia ? naCorrida.get(id) : porId.get(id));
        const vendas = (vendedor) => (doDia ? vendedor.hoje : vendedor.contratos);
        const [sobe, cai] = [quem(cena.sobe), quem(cena.cai)];
        if (!sobe || !cai) return null;

        return {
          ...base,
          tipo: 'ultrapassagem',
          anuncio: `${nomeParaCena(sobe.nome_exibicao)} passou ${nomeParaCena(cai.nome_exibicao)} e assumiu o ${cena.posicao}º lugar ${doDia ? 'de hoje' : 'do período'}.`,
          opcoes: {
            sobe: { ...pessoaDaCena(sobe, vendas(sobe)), antes: cena.antes },
            cai: pessoaDaCena(cai, vendas(cai)),
            posicao: cena.posicao,
            cena: cena.coroa ? 'coroa' : undefined,
            duracao,
          },
        };
      }

      if (cena.tipo === 'marco') {
        const quem = porId.get(cena.id);
        if (!quem) return null;

        return {
          ...base,
          tipo: 'marco',
          anuncio: cena.pct === 100
            ? `${nomeParaCena(quem.nome_exibicao)} bateu a meta.`
            : `${nomeParaCena(quem.nome_exibicao)} chegou a ${cena.pct}% da meta.`,
          opcoes: {
            tipo: 'individual',
            quem: pessoaDaCena(quem, quem.contratos),
            pct: cena.pct,
            marcos: cena.marcos,
            vendas: quem.contratos,
            meta: quem.meta_vendas,
            duracao,
          },
        };
      }

      return {
        ...base,
        tipo: 'equipe',
        anuncio: cena.supermeta ? 'A equipe bateu a supermeta.' : 'A equipe bateu a meta.',
        opcoes: {
          tipo: 'equipe',
          supermeta: cena.supermeta,
          equipe: disputa.slice(0, 8).map((vendedor) => pessoaDaCena(vendedor, vendedor.contratos)),
          vendas: atual.batidas.vendas,
          meta: cena.supermeta ? atual.batidas.superMeta : atual.batidas.meta,
          // A da equipe é sempre a longa: é a cena mais importante, e encurtar vale só para as outras.
          duracao: 'longa',
        },
      };
    }).filter(Boolean);

    if (cenas.length) setFilaDeCenas((atuais) => enfileirarCenas(atuais, cenas));
    else avisarVenda();
  }, [
    loading, dadosDoRecorte, carregandoODia, diaCarregado, erroDoDia, totalVendas, metaTotal, superMetaTotal,
    marcosDeMeta, ranking, daTv, corrida, chaveDoPeriodo, chaveDoDia, palcoLigado, emApresentacao,
  ]);

  const encerrarCena = useCallback((id) => {
    setFilaDeCenas((atuais) => atuais.filter((cena) => cena.id !== id));
  }, []);

  // ===== TEMPORÁRIO: daqui até `testarCena`, mais o botão "Testar cena" e a faixa
  // "Teste (temporário)" lá embaixo. São recursos de demonstração, publicados em 07/10/2026 a pedido
  // para apresentar as cenas. A faixa da mesa aparece só para quem tem `comercial.metas.edit`; o
  // botão da apresentação, para qualquer conta, porque a da TV não tem essa permissão. Depois da
  // apresentação saem do repositório. =====
  /**
   * Uma cena de demonstração, com os nomes do ranking que está na tela.
   *
   * Sem isto, a única forma de conferir som e enquadramento seria esperar uma venda. Nada é gravado,
   * e o ranking não muda.
   */
  /**
   * As faixas que o teste encena: a mais alta abaixo da meta e a mais baixa acima dela, da régua
   * do primeiro vendedor com meta. Sem régua no período, 80% e 120% servem de exemplo.
   */
  const faixasDeTeste = useMemo(() => {
    const quem = ranking.find((vendedor) => vendedor.tem_meta) || ranking[0] || null;
    const daRegua = (marcosDeMeta?.[quem?.tipo_participante] || []).map(Number).filter((faixa) => faixa > 0);
    const todas = [...new Set([...(daRegua.length ? daRegua : [80, 120]), 100])].sort((a, b) => a - b);
    const abaixo = todas.filter((faixa) => faixa < 100);
    const acima = todas.filter((faixa) => faixa > 100);

    return { quem, todas, abaixo: abaixo[abaixo.length - 1] ?? null, acima: acima[0] ?? null };
  }, [ranking, marcosDeMeta]);

  const porNoArCenaDeTeste = (tipo) => {
    const [primeiro, segundo, terceiro] = ranking;
    if (!primeiro) return;

    const base = { id: `teste-${Date.now()}`, anuncio: 'Cena de demonstração.' };
    const sobe = terceiro || segundo || primeiro;
    const cai = segundo || primeiro;
    let cena;

    if (tipo === 'ultrapassagem' || tipo === 'coroa') {
      cena = {
        tipo: 'ultrapassagem',
        chamada: chamadaDaCena({ tipo: 'ultrapassagem', coroa: tipo === 'coroa' }),
        opcoes: {
          sobe: { ...pessoaDaCena(sobe, cai.contratos + 1), antes: cai.contratos },
          cai: pessoaDaCena(cai, cai.contratos),
          posicao: tipo === 'coroa' ? 1 : 2,
          cena: tipo === 'coroa' ? 'coroa' : undefined,
          duracao: 'longa',
        },
      };
    } else if (tipo === 'marco' || tipo === 'faixa' || tipo === 'acima') {
      // A torre mostra as faixas que ficaram para trás, então o teste usa as da régua de verdade.
      const quem = faixasDeTeste.quem;
      const meta = Number(quem.meta_vendas) || quem.contratos || 1;
      const pct = tipo === 'faixa' ? faixasDeTeste.abaixo : tipo === 'acima' ? faixasDeTeste.acima : 100;
      const vendas = Math.ceil((meta * pct) / 100);
      cena = {
        tipo: 'marco',
        chamada: chamadaDaCena({ tipo: 'marco', pct }),
        opcoes: { tipo: 'individual', quem: pessoaDaCena(quem, vendas), pct, marcos: faixasDeTeste.todas, vendas, meta, duracao: 'longa' },
      };
    } else {
      const supermeta = tipo === 'supermeta';
      const meta = (supermeta ? Number(superMetaTotal) : 0) || Number(metaTotal) || totalVendas || 1;
      cena = {
        tipo: 'equipe',
        chamada: chamadaDaCena({ tipo: 'equipe', supermeta }),
        opcoes: {
          tipo: 'equipe',
          supermeta,
          equipe: ranking.slice(0, 8).map((vendedor) => pessoaDaCena(vendedor, vendedor.contratos)),
          vendas: meta,
          meta,
          duracao: 'longa',
        },
      };
    }

    setFilaDeCenas((atuais) => [...atuais, { ...base, ...cena }]);
  };

  // Em apresentação, um botão só: a cada clique vem um tipo diferente.
  const testarCena = () => {
    const tipos = [
      'ultrapassagem',
      'coroa',
      ...(faixasDeTeste.abaixo ? ['faixa'] : []),
      'marco',
      ...(faixasDeTeste.acima ? ['acima'] : []),
      'equipe',
      'supermeta',
    ];
    porNoArCenaDeTeste(tipos[cenaDeTeste.current % tipos.length]);
    cenaDeTeste.current += 1;
  };

  // Fora da apresentação a cena cobre a janela de quem está trabalhando: Esc a tira do ar.
  const idDaCenaNoAr = filaDeCenas[0]?.id || null;
  useEffect(() => {
    if (!idDaCenaNoAr || emApresentacao) return undefined;

    const aoTeclar = (event) => {
      if (event.key === 'Escape') setFilaDeCenas((atuais) => atuais.filter((cena) => cena.id !== idDaCenaNoAr));
    };
    window.addEventListener('keydown', aoTeclar);

    return () => window.removeEventListener('keydown', aoTeclar);
  }, [idDaCenaNoAr, emApresentacao]);

  const alternarCenas = () => {
    if (naApresentacao) {
      setCenasDaApresentacao((ligadas) => !ligadas);
      return;
    }
    setCenasDaMesa((ligadas) => {
      gravarPreferencia(CHAVE_DAS_CENAS, !ligadas);
      return !ligadas;
    });
  };

  const alternarSom = () => setSomLigado((ligado) => {
    gravarPreferencia(CHAVE_DO_SOM, !ligado);
    return !ligado;
  });

  const encerrarCelebracao = useCallback((id) => {
    setCelebracoes((atuais) => atuais.filter((item) => item.id !== id));
  }, []);

  const handleManualRefresh = async () => {
    triggerRefreshFeedback();
    // O botão CONTINUA forçando: é onde "quero o número agora" pertence, e é ação de alguém
    // presente, não um laço automático.
    const [ok] = await Promise.all([recarregar({ force: true }), recarregarODia({ force: true })]);
    setFalhasSeguidas((anteriores) => (ok ? 0 : anteriores + 1));
    setLastPollingRefresh(new Date());
  };

  const handleFotoAtualizada = () => {
    triggerRefreshFeedback();
    recarregar({ force: true });
    recarregarODia({ force: true });
    setLastPollingRefresh(new Date());
  };

  useEffect(() => () => {
    if (refreshFeedbackTimer.current) {
      clearTimeout(refreshFeedbackTimer.current);
    }
  }, []);

  // TV parada não mostra controle nem cursor: eles só voltam quando alguém mexe.
  useEffect(() => {
    if (!isFakeFullscreen) {
      setOcioso(false);
      return undefined;
    }

    let espera;
    const acordar = () => {
      setOcioso(false);
      clearTimeout(espera);
      espera = setTimeout(() => setOcioso(true), OCIOSO_APOS_MS);
    };
    const eventos = ['mousemove', 'pointerdown', 'keydown', 'touchstart'];

    acordar();
    eventos.forEach((evento) => window.addEventListener(evento, acordar, { passive: true }));

    return () => {
      clearTimeout(espera);
      eventos.forEach((evento) => window.removeEventListener(evento, acordar));
    };
  }, [isFakeFullscreen]);

  useEffect(() => {
    if (!isFakeFullscreen) return undefined;

    const aoRedimensionar = () => setAlturaDaJanela(window.innerHeight);
    aoRedimensionar();
    window.addEventListener('resize', aoRedimensionar);

    return () => window.removeEventListener('resize', aoRedimensionar);
  }, [isFakeFullscreen]);

  useEffect(() => {
    if (!isFakeFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsFakeFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFakeFullscreen]);

  const handleToggleFullscreen = () => {
    setIsFakeFullscreen((current) => !current);
  };

  const metaTotalBatida = Number(metaTotal) > 0 && Number(totalVendas) >= Number(metaTotal);
  const temSuperMeta = Number(superMetaTotal) > 0;
  const superMetaBatida = temSuperMeta && Number(totalVendas) >= Number(superMetaTotal);

  const ultimoPolling = lastPollingRefresh
    ? lastPollingRefresh.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : ultimaAtualizacao?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) || 'aguardando';
  const isPresentationMode = isFakeFullscreen;
  // A hora é a da última carga que DEU CERTO. `lastPollingRefresh` anda também quando a consulta
  // falha, e mostrar essa hora como "atualizado" seria a mesma mentira do ponto verde.
  const horaDoDado = horaCurta(ultimaAtualizacao);
  const desatualizado = Boolean(error);
  const classeDeOcioso = `transition-opacity duration-300 focus-within:pointer-events-auto focus-within:opacity-100 ${ocioso ? 'pointer-events-none opacity-0' : 'opacity-100'}`;
  const classeDoBotaoRedondo = 'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-slate-950/25 backdrop-blur-xl transition duration-200 hover:border-white/20 hover:bg-white/[0.11] hover:text-white focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-slate-950';
  // O som vale para a TV e para a mesa, e a escolha fica guardada no navegador. As cenas, só na mesa.
  const botaoDeSom = cenasLigadas && (
    <button
      type="button"
      onClick={alternarSom}
      aria-pressed={somLigado}
      className={`${classeDoBotaoRedondo} text-white/70`}
      title={somLigado ? 'Desligar o som das cenas' : 'Ligar o som das cenas'}
      aria-label="Som das cenas"
    >
      {somLigado ? <Volume2 className="h-4 w-4" aria-hidden="true" /> : <VolumeX className="h-4 w-4" aria-hidden="true" />}
    </button>
  );
  const botaoDeCenas = (
    <button
      type="button"
      onClick={alternarCenas}
      aria-pressed={cenasLigadas}
      className={`${classeDoBotaoRedondo} ${cenasLigadas ? 'text-white/70' : 'text-white/35'}`}
      title={cenasLigadas ? 'Desligar as cenas de ultrapassagem e de meta' : 'Ligar as cenas de ultrapassagem e de meta'}
      aria-label="Cenas de ultrapassagem e de meta"
    >
      <Clapperboard className="h-4 w-4" aria-hidden="true" />
    </button>
  );
  const controleDeAtualizacao = (
    <PollingIntervalControl
      value={pollingInterval}
      onChange={setPollingInterval}
      onRefresh={handleManualRefresh}
      refreshing={loading || refreshFeedback}
      lastRefreshLabel={ultimoPolling}
      variant="compact"
      minValue={POLLING_MINIMO_RANKING_MS}
      hint={`Atualiza o ranking a cada ${formatPollingInterval(intervaloEfetivo)}. O botão ao lado força uma consulta nova; o ciclo automático aproveita o cache de 5 min.`}
    />
  );
  // Com a corrida de hoje embaixo, pódio e lista dividem a altura da TV com ela e encolhem.
  const compacto = isPresentationMode && mostrarCorrida;

  const cardsDaTv = daTv.vendedores.slice(0, MAXIMO_DE_RAIAS);
  const lugaresDaTv = ordemDoPodioSimetrico(cardsDaTv.length);
  const foraDaTv = daTv.vendedores.length - cardsDaTv.length;
  const fullscreenStyle = isPresentationMode
    ? {
        borderRadius: 0,
        height: ALTURA_DA_TELA,
        overflow: 'hidden',
        padding: 'clamp(24px, 3vw, 52px)',
        width: '100vw',
      }
    : {};
  const containerClass = isPresentationMode
    ? 'ranking-comercial fixed top-0 right-0 bottom-0 left-0 z-[9999] h-screen w-screen overflow-hidden rounded-none border-0 bg-slate-950 p-4 text-white shadow-[0_24px_90px_rgba(2,6,23,0.42)] sm:p-6 lg:p-8'
    : fullscreen
      ? 'ranking-comercial relative min-h-[calc(100dvh-190px)] overflow-hidden rounded-[32px] border border-white/10 bg-slate-950 p-4 text-white shadow-[0_24px_90px_rgba(2,6,23,0.42)] sm:p-6 lg:p-8'
      : 'ranking-comercial relative overflow-hidden rounded-[28px] border border-white/10 bg-slate-950 p-4 text-white shadow-[0_24px_80px_rgba(2,6,23,0.38)] sm:p-6 lg:p-8';
  const contentWrapperClass = isPresentationMode
    ? 'relative z-10 mx-auto flex h-full min-h-0 w-full max-w-[1780px] flex-col'
    : 'relative z-10';
  const headerClass = isPresentationMode
    ? 'mb-6 flex shrink-0 flex-col gap-5 pr-14 sm:pr-16 xl:flex-row xl:items-end xl:justify-between'
    : 'mb-7 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between';
  const mainGridClass = isPresentationMode
    ? 'relative grid min-h-0 flex-1 items-center gap-6 xl:grid-cols-[1.12fr_0.88fr] 2xl:gap-8'
    : 'grid gap-5 xl:grid-cols-[1.08fr_0.92fr]';
  const podiumPanelClass = isPresentationMode
    ? 'relative flex h-[clamp(520px,calc(100dvh-290px),790px)] min-h-0 flex-col overflow-hidden rounded-[34px] border border-white/[0.16] bg-slate-950/[0.48] p-6 shadow-[0_28px_90px_rgba(2,6,23,0.38)] backdrop-blur-xl xl:p-8'
    : 'rounded-3xl border border-white/[0.14] bg-slate-950/[0.44] p-4 backdrop-blur-xl sm:p-6';
  const podiumGridClass = isPresentationMode
    ? 'grid min-h-0 flex-1 gap-5 py-5 lg:grid-cols-3 lg:items-center xl:gap-6 xl:py-6'
    : 'grid min-h-[420px] gap-4 lg:grid-cols-3 lg:items-end';
  const rankingPanelClass = isPresentationMode
    ? 'relative flex h-[clamp(520px,calc(100dvh-290px),790px)] min-h-0 flex-col overflow-hidden rounded-[34px] border border-white/[0.16] bg-slate-950/[0.48] p-6 shadow-[0_28px_90px_rgba(2,6,23,0.34)] backdrop-blur-xl xl:p-8'
    : 'rounded-3xl border border-white/[0.14] bg-slate-950/[0.44] p-4 backdrop-blur-xl sm:p-6';
  const rankingListClass = isPresentationMode
    ? 'min-h-0 flex-1 space-y-4 overflow-y-auto pr-1'
    : 'max-h-[560px] space-y-3 overflow-y-auto pr-1';
  const headingClass = isPresentationMode
    ? 'text-4xl font-black leading-tight tracking-normal text-white xl:text-6xl'
    : 'text-3xl font-black leading-tight tracking-normal text-white sm:text-4xl';
  const controlsClass = isPresentationMode
    ? 'flex w-full flex-col gap-3 sm:items-start xl:w-auto xl:min-w-[720px] xl:flex-row xl:items-center xl:justify-end'
    : 'flex flex-col gap-3 sm:items-start lg:flex-row lg:items-center lg:justify-end';
  // Quatro colunas quando há supermeta: ela é um ALVO, como a meta, e pertence à mesma fileira.
  const kpiGridClass = isPresentationMode
    ? `grid w-full ${temSuperMeta ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'} gap-2 rounded-[28px] border border-white/[0.10] bg-white/[0.045] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl xl:w-auto`
    : `grid w-full ${temSuperMeta ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'} gap-2 rounded-3xl border border-white/[0.10] bg-white/[0.045] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl sm:w-auto`;
  const kpiCardClass = isPresentationMode
    ? 'min-w-0 rounded-2xl border border-white/[0.08] bg-white/[0.055] px-4 py-3 transition duration-200 hover:bg-white/[0.08] sm:min-w-[132px]'
    : 'min-w-0 rounded-2xl border border-white/[0.08] bg-white/[0.055] px-3 py-2.5 transition duration-200 hover:bg-white/[0.08] sm:min-w-[116px]';
  const kpiValueClass = isPresentationMode
    ? 'mt-1 text-2xl font-black tabular-nums text-white'
    : 'mt-1 text-xl font-black tabular-nums text-white';
  const rankingItemClass = isPresentationMode
    ? 'group rounded-[24px] border border-white/[0.12] bg-white/[0.065] p-5 transition duration-200 hover:border-white/[0.26] hover:bg-white/[0.09]'
    : 'group rounded-2xl border border-white/[0.12] bg-white/[0.065] p-4 transition duration-200 hover:border-white/[0.26] hover:bg-white/[0.09]';
  const rankingNameClass = isPresentationMode
    ? 'truncate text-base font-black text-white xl:text-lg'
    : 'truncate text-sm font-black text-white';
  const rankingValueClass = isPresentationMode
    ? 'text-base font-black tabular-nums text-white xl:text-lg'
    : 'text-sm font-black tabular-nums text-white';
  const sectionIconClass = isPresentationMode
    ? 'inline-flex h-12 w-12 items-center justify-center rounded-2xl'
    : 'inline-flex h-10 w-10 items-center justify-center rounded-2xl';
  const sectionTitleClass = isPresentationMode
    ? 'text-xl font-black text-white xl:text-2xl'
    : 'text-lg font-black text-white';
  const emptyStateClass = isPresentationMode
    ? 'flex min-h-0 flex-1 flex-col items-center justify-center rounded-3xl border border-white/[0.12] bg-white/[0.055] px-6 text-center'
    : 'flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-white/[0.12] bg-white/[0.055] px-6 text-center';

  if (loading && chaveDosDados === null) {
    return (
      <section className={fullscreen ? 'relative min-h-[calc(100dvh-190px)] overflow-hidden rounded-[32px] bg-slate-950 p-4 text-white sm:p-6' : 'relative overflow-hidden rounded-[28px] bg-slate-950 p-4 text-white sm:p-6'}>
        <RankingSkeleton />
      </section>
    );
  }

  return (
    <section
      className={`${containerClass} ${ocioso ? 'cursor-none' : ''}`}
      style={{
        background: FUNDO_DO_PAINEL,
        ...fullscreenStyle,
      }}
    >
      {/* Na TV o fundo é liso: a grade e as manchas desfocadas só disputavam a tela com os rostos e
          os números, e uma imagem parada por horas com enfeite claro é a que mais marca o painel. */}
      {!compacto && (
        <>
        <div className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:56px_56px]" />
        <div className="pointer-events-none absolute -left-28 top-24 h-72 w-72 rounded-full bg-[color:var(--accent)]/20 blur-3xl" style={{ animation: 'float-blob 9s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 translate-x-1/3 translate-y-1/3 rounded-full bg-white/10 blur-3xl" style={{ animation: 'float-blob 11s ease-in-out infinite reverse' }} />
        </>
      )}

      <button
        type="button"
        onClick={handleToggleFullscreen}
        className={`${classeDeOcioso} ${isPresentationMode ? 'top-1' : 'top-4 sm:top-5'} group absolute right-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-slate-950/25 text-white/70 shadow-[0_12px_40px_rgba(2,6,23,0.22)] backdrop-blur-xl transition duration-200 hover:border-white/20 hover:bg-white/[0.11] hover:text-white active:scale-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-slate-950 sm:right-5`}
        title={isPresentationMode ? 'Sair do modo apresentação' : 'Entrar em modo apresentação'}
        aria-label={isPresentationMode ? 'Sair do modo apresentação' : 'Entrar em modo apresentação'}
      >
        {isPresentationMode ? (
          <Minimize2 className="h-4 w-4 transition duration-200 group-hover:text-[color:var(--accent)]" />
        ) : (
          <Maximize2 className="h-4 w-4 transition duration-200 group-hover:text-[color:var(--accent)]" />
        )}
      </button>

      {/* Cenas e som só existem em apresentação, então os controles também. */}
      {(isPresentationMode || fullscreen) && (
        <div className={`${classeDeOcioso} ${isPresentationMode ? 'top-1' : 'top-4 sm:top-5'} absolute right-[4.25rem] z-20 flex items-center space-x-2 sm:right-[4.75rem]`}>
          {/* Na TV o controle de atualização sai do cabeçalho e vem para cá: é coisa de quem está
              com o mouse na mão, e some junto com os outros quando ninguém mexe. */}
          {compacto && (
            <div className="rounded-full bg-slate-950/80 px-1 backdrop-blur-xl">{controleDeAtualizacao}</div>
          )}
          {/* Sem exigir permissão: a conta da TV, onde a cena precisa ser conferida, não edita metas. */}
          {cenasAtivas && ranking.length > 0 && (
            <button
              type="button"
              onClick={testarCena}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-white/10 bg-slate-950/25 px-4 text-sm font-bold text-white/70 backdrop-blur-xl transition duration-200 hover:border-white/20 hover:bg-white/[0.11] hover:text-white focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-slate-950"
              title="Mostra uma cena de demonstração com os nomes do ranking atual. Nada é gravado."
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Testar cena
            </button>
          )}
          {botaoDeSom}
          {botaoDeCenas}
        </div>
      )}

      <div className={contentWrapperClass} style={compacto ? { zoom: escalaDaTv(alturaDaJanela) } : undefined}>
        {compacto ? (
          <CabecalhoDaTv
            empresa={empresaAtual}
            periodo={getPeriodo(filtroData)}
            vendas={Number(totalVendas) || 0}
            meta={Number(metaTotal) || 0}
            superMeta={Number(superMetaTotal) || 0}
            hora={horaDoDado}
            desatualizado={desatualizado}
          />
        ) : (
        <header className={headerClass}>
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-1 text-xs font-semibold uppercase text-white/[0.62]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Desempenho comercial
            </div>
            <h2 className={headingClass}>
              Ranking de Vendedores
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-white/60">
              {empresaAtual} · {getPeriodo(filtroData)}
              {horaDoDado && <> · <EstadoDoDado hora={horaDoDado} desatualizado={desatualizado} /></>}
            </p>
          </div>

          <div className={controlsClass}>
            <div className={kpiGridClass}>
              <div className={kpiCardClass}>
                <div className="flex min-w-0 items-center gap-2 text-[11px] font-semibold text-white/[0.52]">
                  <Target className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent)]" />
                  Vendas
                </div>
                <p className={kpiValueClass}>{numero(totalVendas)}</p>
              </div>
              <div className={kpiCardClass}>
                <div className="flex min-w-0 items-center gap-2 text-[11px] font-semibold text-white/[0.52]">
                  <Award className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent)]" />
                  Top 3
                </div>
                <p className={kpiValueClass}>{numero(topTres.reduce((total, vendedor) => total + vendedor.contratos, 0))}</p>
              </div>
              {/* Meta batida muda o CARD, não só o número: é o estado que fica depois do confete. */}
              <div className={`${kpiCardClass} ${metaTotalBatida ? 'border-[color:var(--status-good)]/50 bg-[color:var(--status-good)]/[0.10]' : ''}`}>
                <div className={`flex min-w-0 items-center gap-2 text-[11px] font-semibold ${metaTotalBatida ? 'text-[color:var(--status-good)]' : 'text-white/[0.52]'}`}>
                  <Target className={`h-3.5 w-3.5 shrink-0 ${metaTotalBatida ? 'text-[color:var(--status-good)]' : 'text-[color:var(--accent)]'}`} />
                  Meta total
                </div>
                <p className={kpiValueClass}>{numero(metaTotal)}</p>
                {metaTotalBatida && <p className="mt-0.5 text-[11px] font-semibold text-[color:var(--status-good)]">batida</p>}
              </div>
              {/* A supermeta é um alvo próprio, e por isso um card próprio: o que falta para ela é a
                  pergunta que a equipe faz depois de bater a meta. */}
              {temSuperMeta && (
                <div className={`${kpiCardClass} ${superMetaBatida ? 'border-amber-300/60 bg-amber-300/[0.10]' : ''}`}>
                  <div className={`flex min-w-0 items-center gap-2 text-[11px] font-semibold ${superMetaBatida ? 'text-amber-200/85' : 'text-white/[0.52]'}`}>
                    <Trophy className={`h-3.5 w-3.5 shrink-0 ${superMetaBatida ? 'text-amber-300' : 'text-amber-300/70'}`} />
                    Supermeta
                  </div>
                  <p className={`${kpiValueClass} ${superMetaBatida ? 'text-amber-200' : ''}`}>{numero(superMetaTotal)}</p>
                  <p className={`mt-0.5 text-[11px] font-semibold ${superMetaBatida ? 'text-amber-200' : 'text-white/[0.45]'}`}>
                    {superMetaBatida ? 'batida' : `faltam ${numero(Math.max(0, Number(superMetaTotal) - Number(totalVendas)))}`}
                  </p>
                </div>
              )}
            </div>
            {controleDeAtualizacao}
            {!isPresentationMode && (
              <div className="flex items-center gap-2">
                {botaoDeSom}
                {botaoDeCenas}
              </div>
            )}
          </div>
        </header>
        )}

        {/*
          A indisponibilidade da origem precisa aparecer MESMO com dado na tela.
          Antes, o erro só era exibido no estado vazio: uma atualização que falhasse deixava o
          ranking anterior no lugar, sem aviso nenhum, e o único sinal ficava no console. Com a
          leitura vindo direto do IXC isso é pior — um 503 significa que o número exibido é de
          antes, e quem olha a tela não tem como saber. Sem retry automático: o botão é explícito.
        */}
        {/*
          TEMPORÁRIO — para a apresentação das cenas; sai depois dela.
          Botões para ver as comemorações de meta e de faixa sem esperar acontecer. Só para quem tem
          `comercial.metas.edit`: a equipe de vendas não vê a faixa.
          As duas de FAIXA só tocam na TV em uso real; aqui o botão as põe no ar na mesa para conferir.
        */}
        {!isPresentationMode && palcoLigado && podeEditarFotos && ranking.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-amber-300/40 bg-amber-300/[0.06] px-4 py-3 text-sm text-amber-100">
            <span className="mr-1 font-semibold">Teste (temporário):</span>
            {[
              ...(faixasDeTeste.abaixo ? [['faixa', `Faixa de ${numero(faixasDeTeste.abaixo)}%`]] : []),
              ['marco', 'Meta individual'],
              ...(faixasDeTeste.acima ? [['acima', `Faixa de ${numero(faixasDeTeste.acima)}%`]] : []),
              ['equipe', 'Meta da equipe'],
              ['supermeta', 'Supermeta'],
            ].map(([tipo, rotulo]) => (
              <button
                key={tipo}
                type="button"
                onClick={() => porNoArCenaDeTeste(tipo)}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-amber-300/40 px-4 font-semibold text-amber-100 transition hover:bg-amber-300/10 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                {rotulo}
              </button>
            ))}
            <span className="text-amber-100/80">Esc encerra a cena.</span>
          </div>
        )}

        {/* Na TV o aviso vai para o cabeçalho ("Sem atualizar desde 12:28"): uma faixa a mais
            empurraria o pódio e a pista, que ali têm a altura contada. */}
        {error && ranking.length > 0 && !compacto && (
          <div
            role="alert"
            className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/[0.08] px-4 py-3 text-sm text-amber-100"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-300" />
            <span className="min-w-0 flex-1">
              Não foi possível atualizar o ranking. Os números abaixo são da última atualização
              bem-sucedida{ultimaAtualizacao ? ` (${ultimaAtualizacao.toLocaleTimeString('pt-BR')})` : ''}.
              {falhasSeguidas > 1 && (
                <> Novas tentativas automáticas a cada {formatPollingInterval(intervaloEfetivo)}.</>
              )}
            </span>
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={loading}
              className="shrink-0 rounded-full border border-amber-300/40 px-3 py-1 text-xs font-semibold text-amber-100 transition hover:bg-amber-300/10 disabled:opacity-50"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {aguardandoRecorte ? (
          <div className={mainGridClass} aria-busy="true" aria-label="Carregando o ranking">
            <div className={`${podiumPanelClass} min-h-[420px] animate-pulse`} />
            <div className={`${rankingPanelClass} min-h-[420px] animate-pulse`} />
          </div>
        ) : ranking.length === 0 ? (
          <div className={emptyStateClass}>
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-[color:var(--accent)]">
              <Award className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-black text-white">Nenhum vendedor encontrado</h3>
            <p className="mt-2 max-w-md text-sm text-white/[0.58]">
              {error || 'Quando a API retornar o ranking do período, o pódio e a lista completa aparecem aqui.'}
            </p>
          </div>
        ) : compacto ? (
          // Sem painel em volta: os cards ficam direto no fundo. Card dentro de card era moldura
          // dupla, e a de fora não dizia nada.
          <section className="flex min-h-0 flex-1 flex-col">
            <TituloDaTv icone={Trophy} titulo="Ranking do período" desatualizado={desatualizado}>
              {daTv.somenteInternos ? 'Vendedores internos' : 'Vendedores'}
              {foraDaTv > 0 ? ` · e mais ${numero(foraDaTv)}` : ''}
            </TituloDaTv>
            {/* No HTML a lista continua do 1º ao último, que é como um leitor de tela deve lê-la; só a
                posição NA TELA é a do pódio (`order`). A coluna do líder é um pouco mais larga. */}
            <ol
              className="grid min-h-0 flex-1 items-end justify-items-center gap-4"
              style={{ gridTemplateColumns: lugaresDaTv.map((posicao) => `minmax(0, ${posicao === 1 ? 1.15 : 1}fr)`).join(' ') }}
            >
              {cardsDaTv.map((vendedor, index) => (
                <CardDaTv
                  key={vendedor.id}
                  vendedor={vendedor}
                  posicao={index + 1}
                  lugar={lugaresDaTv.indexOf(index + 1)}
                  medida={cardsDaTv.length <= 4 ? 'folgado' : cardsDaTv.length === 5 ? 'medio' : 'justo'}
                />
              ))}
            </ol>
          </section>
        ) : (
          <div className={mainGridClass}>
            {isPresentationMode && (
              <div className="pointer-events-none absolute left-[60.5%] top-1/2 z-0 hidden h-px w-28 -translate-x-1/2 bg-[linear-gradient(90deg,rgba(250,204,21,0),rgba(250,204,21,0.38),rgba(255,255,255,0))] xl:block" />
            )}
            <section className={podiumPanelClass}>
              <div className="mb-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className={`${sectionIconClass} bg-amber-300/[0.15] text-amber-300`}>
                    <Trophy className={isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} />
                  </span>
                  <div className="flex items-center gap-2">
                    <h3 className={sectionTitleClass}>Pódio do período</h3>
                    <PontoAoVivo desatualizado={desatualizado} />
                  </div>
                </div>
              </div>

              <div className={podiumGridClass}>
                {topTres.map((vendedor, index) => (
                  <PodiumCard
                    key={vendedor.id}
                    vendedor={vendedor}
                    posicao={index + 1}
                    destaque={index === 0}
                    isFullscreen={isPresentationMode}
                  />
                ))}
              </div>
            </section>

            <section className={rankingPanelClass}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className={`${sectionIconClass} bg-white/10 text-[color:var(--accent)]`}>
                    <Award className={isPresentationMode ? 'h-6 w-6' : 'h-5 w-5'} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={sectionTitleClass}>Ranking completo</h3>
                      <PontoAoVivo desatualizado={desatualizado} atraso="0.4s" />
                    </div>
                    <p className="text-xs text-white/50">Quantidade de vendas no período</p>
                  </div>
                </div>
                <span className="hidden text-xs font-semibold uppercase text-white/[0.42] sm:inline">
                  Vendas
                </span>
              </div>

              <div className={rankingListClass}>
                {ranking.map((vendedor, index) => {
                  const progresso = Math.max(8, Math.round((vendedor.contratos / maiorQuantidade) * 100));

                  return (
                    <article
                      key={vendedor.id}
                      className={rankingItemClass}
                      style={{
                        animation: 'ranking-fade-up 380ms ease-out both',
                        animationDelay: `${Math.min(index * 55, 500)}ms`,
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className={getPositionBadgeClass(index, isPresentationMode)}>
                          {index + 1}
                        </span>
                        <div className="relative shrink-0">
                          <VendedorAvatar
                            vendedor={vendedor}
                            className={isPresentationMode ? 'h-14 w-14' : 'h-12 w-12'}
                            textClassName={isPresentationMode ? 'text-base' : 'text-sm'}
                          />
                          {metaBatidaDoVendedor(vendedor) && <SeloMetaBatida isFullscreen={isPresentationMode} />}
                          {podeEditarFotos && Number.isFinite(Number(vendedor.id)) && (
                            <button
                              type="button"
                              onClick={() => setVendedorFotoEmEdicao({
                                ...vendedor,
                                empresaFoto: vendedor.empresas?.[0] || empresaEdicaoFotos,
                              })}
                              className="absolute -bottom-1 -right-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/[0.15] bg-slate-950/90 text-white/70 opacity-100 shadow-lg transition hover:bg-[color:var(--accent)] hover:text-white focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                              title={`Editar foto de ${vendedor.nome}`}
                              aria-label={`Editar foto de ${vendedor.nome}`}
                            >
                              <Camera className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <h4 className={rankingNameClass} title={vendedor.nome}>{vendedor.nome_exibicao || vendedor.nome}</h4>
                            </div>
                            <div className="text-right">
                              <p className={rankingValueClass}>{vendasPorExtenso(vendedor.contratos)}</p>
                              {/* Sem meta e fora da equipe são coisas diferentes: a segunda trava o
                                  fechamento da comissão, e antes as duas ficavam iguais na tela. */}
                              {!vendedor.tem_meta && vendedor.fora_da_equipe && (
                                <p className="mt-1 text-xs font-medium text-[color:var(--status-warn)]" title="Vendeu no período mas não foi cadastrado na equipe da competência. Estas vendas bloqueiam o fechamento da comissão.">
                                  Fora da equipe da competência
                                </p>
                              )}
                            </div>
                          </div>

                          {vendedor.tem_meta ? (
                            <ProgressoDaMeta
                              percentual={vendedor.percentual_meta}
                              meta={vendedor.meta_vendas}
                              contratos={vendedor.contratos}
                            />
                          ) : (
                            // Sem meta não há o que atingir: a barra volta a ser só comparação com a
                            // líder, e o rótulo diz isso — antes ela ficava idêntica à de quem tem
                            // meta, e as duas pareciam a mesma medida.
                            <div className={isPresentationMode ? 'mt-4' : 'mt-3'}>
                              <span className="text-[11px] font-medium text-white/[0.4]">
                                {Math.round((vendedor.contratos / maiorQuantidade) * 100)}% do primeiro colocado
                              </span>
                              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.12]">
                                <div
                                  className="h-full rounded-full bg-white/[0.28] transition-[width] duration-500"
                                  style={{ width: `${progresso}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {mostrarCorrida && (
          <CorridaDeHoje
            raias={corrida.raias}
            somenteInternos={corrida.somenteInternos}
            dia={formatarData(hoje)}
            compacto={compacto}
            estado={estadoDoDia}
            vendasNovas={vendasNaPista}
            metaChegada={corrida.metaChegada}
            supermetaChegada={corrida.supermetaChegada}
            metasDoDia={metasNaPista}
            metaIndisponivel={diaCarregado && Boolean(metaDiariaDoDia?.indisponivel)}
          />
        )}
      </div>

      <CelebracaoDeMeta eventos={celebracoes} onEncerrar={encerrarCelebracao} />

      {palcoLigado && (
        <CenasDoRanking fila={filaDeCenas} som={somLigado} onEncerrar={encerrarCena} telaInteira={!emApresentacao} empresa={empresaDasCenas} />
      )}
      {/* A cena é só imagem; para o leitor de tela o fato vai por escrito, uma vez. */}
      <p className="sr-only" role="status">{filaDeCenas[0]?.anuncio || ''}</p>

      <style>{`
        @keyframes celebracao-entrada {
          from { opacity: 0; transform: translateY(-22px) scale(0.9); }
          60%  { opacity: 1; transform: translateY(4px)   scale(1.02); }
          to   { opacity: 1; transform: translateY(0)     scale(1); }
        }
        @keyframes celebracao-clarao {
          0%   { opacity: 0; }
          18%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes celebracao-trofeu {
          0%, 100% { transform: scale(1)    rotate(-4deg); }
          50%      { transform: scale(1.08) rotate(4deg); }
        }
        /* Só um véu, sem desfoque: desfocar a tela inteira por baixo de um canvas que redesenha a
           cada quadro pesa justamente nas TVs e nos aparelhos fracos em que este painel roda. */
        .ranking-cenas-palco.ultrapassagem-no-ar { background: var(--ranking-cena-veu, rgba(20, 0, 35, 0.82)); }
        @keyframes ranking-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer-slide {
          from { transform: translateX(-100%); }
          to   { transform: translateX(250%); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);    opacity: 0.55; }
          100% { transform: scale(1.65); opacity: 0; }
        }
        @keyframes float-blob {
          0%, 100% { transform: translate(0, 0)      scale(1); }
          40%       { transform: translate(18px, -14px) scale(1.06); }
          70%       { transform: translate(-8px, 10px)  scale(0.96); }
        }
        @keyframes live-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.28; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ranking-comercial *,
          .ranking-comercial *::before,
          .ranking-comercial *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <EditorDeFotoDoVendedor
        isOpen={Boolean(vendedorFotoEmEdicao)}
        vendedor={vendedorFotoEmEdicao}
        empresa={empresaEdicaoFotos}
        onClose={() => setVendedorFotoEmEdicao(null)}
        onSaved={handleFotoAtualizada}
      />
    </section>
  );
};

export default RankingComercialTab;
